<script lang="ts">
export default {
  name: 'StoreConnects',
}
</script>
<script setup lang="ts">
import { computed, watch, ref, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useLocationConnectStore } from '../../domains/location/stores'
import { useCompaniesStoreDocomo } from '../../domains/location/stores'

import {
  KkBox,
  KkLoading,
  KkSelect,
} from '../../components/base'
import {
  KkInfo,
} from '../../components/ui'
import {
  KkConnectSite,
  KkUpgradeModal,
} from '../../domains/locationStore'

/** Interface */
interface ReadLocationStoreParams {
  company_uuid: string
  store_uuid: string
}
interface ReadSitesParams {
  company_uuid: string
  store_uuid: string
}
interface ReadStoreGmbParams {
  company_uuid: string
  store_uuid: string
}
interface Site {
  businessTypes?: number[]
  name: string
  siteType: string
  connected?: boolean
  canConnectStoreIdentifier: boolean
}

/** Variable */
const store = useStore()
const route = useRoute()
const { t } = useI18n()
const emit = defineEmits(['update-title'])
const isBusinessType = ref<number>(0)
const isOpenUpgradeModal = ref<boolean>(false)
const openUpgradeModalService = ref<string>('google')
const businessTypes = reactive<{[key: string]: number}>({
  'すべての口コミサイト': 0,
})

/** Computed */
const companyUuid = computed<string>(() => route.params.company_uuid as string)
const storeUuid = computed<string>(() => route.params.store_uuid as string)
const tabs = computed<string[]>(() => {
  const tabs = isRettyPlan.value ? ['connection'] : ['information', 'connection']
  if (providedFeaturesStoreOption.value) tabs.push('option')
  if (providedFeaturesStoreSocial.value) tabs.push('sns')
  if (providedFeaturesCompetitor.value) tabs.push('competitor')
  if (providedFeaturesFoodMenu.value && canHaveFoodMenus.value) tabs.push('foodMenu')
  if (hasAvailableProduct.value && providedFeaturesProduct.value) tabs.push('product')
  if (providedFeaturesServicePattern.value && canModifyServiceList.value) tabs.push('service')
  return tabs
})
const filteredSites = computed<Site[]>(() => {
  const items = []
  // NOTE: 外部サイト連携できないプランの場合は表示しない
  // ただし、Google連携は表示したいため追加する
  if(providedFeaturesExternalAccount.value && providedFeaturesLinkUrl.value) {
    items.push(...
    sites.value
      .filter(site => {
        const hasAccount = connects.value.meta.accountSiteTypes.includes(site.siteType)
        const hasReview = connects.value.meta.reviewSiteTypes.includes(site.siteType)
        const hasStoreId = connects.value.meta.accountStoreIdentifierSiteTypes.includes(site.siteType)
        const canDisplay  = !isRettyPlan.value || ['google', 'trip_advisor', 'retty'].includes(site.siteType)
        return canDisplay && (hasAccount || hasReview || hasStoreId)
      })
      .filter(site => {
        if (isBusinessType.value === 0) return true
        if (site.businessTypes) {
          return site.businessTypes.includes(isBusinessType.value)
        } else {
          return false
        }
      })
      .filter(site => {
        if (site.siteType !== 'taishu_tenpyo') return true
        return taishuTenpyoOption.value
      })
      .map(site => {
        const canConnectStoreIdentifier = connects.value.meta.accountStoreIdentifierSiteTypes.includes(site.siteType)
        return { ...site, canConnectStoreIdentifier }
      })
    )
  } else {
    const google = {
      name: 'Google マップ',
      siteType: 'google',
      connected: storeGmbConnected.value,
      canConnectStoreIdentifier: false,
    }
    items.push(google)
  }

  if (canDisplayDocomo.value) {
    const docomo = {
      name: 'docomo',
      siteType: 'docomo',
      connected: Boolean(storeDocomo.value?.docomoStoreId),
      storeId: storeDocomo.value?.docomoStoreId,
      canConnectStoreIdentifier: false,
    }
    items.push(docomo)
  }
  if (canDisplayShufoo.value) {
    const shufoo = {
      name: 'Shufoo!',
      siteType: 'shufoo',
      connected: Boolean(storeOcShufoo.value?.shufooId),
      storeId: storeOcShufoo.value?.shufooId,
      canConnectStoreIdentifier: false,
    }
    items.push(shufoo)
  }
  if (canDisplayMapionBiz.value) {
    const mapionBiz = {
      name: 'Mapion Biz',
      siteType: 'mapion_biz',
      connected: Boolean(storeOcMapionBiz.value?.mapionBizId),
      storeId: storeOcMapionBiz.value?.mapionBizId,
      canConnectStoreIdentifier: false,
    }
    items.push(mapionBiz)
  }
  return items
})
const canDisplayDocomo = computed<boolean>(() => {
  return hasDocomoStore.value
})
const canDisplayShufoo = computed<boolean>(() => {
  return showOnlyOwnerAuthority.value && (isAdmin.value || isDistributer.value) && providedFeaturesShufoo.value
})
const canDisplayMapionBiz = computed<boolean>(() => {
  return showOnlyOwnerAuthority.value && (isAdmin.value || isDistributer.value) && providedFeaturesMapionBiz.value
})

/** Pinia Store */
const locationConnectStore = useLocationConnectStore()
const { connects, isLoading } = storeToRefs(locationConnectStore)
const { readConnects } = locationConnectStore
const companiesStoreDocomo = useCompaniesStoreDocomo()
const { storeDocomo, isLoading: storeDocomoLoading } = storeToRefs(companiesStoreDocomo)
const { readStoreDocomo } = companiesStoreDocomo

/** Vuex Store: state */
const locationStore = computed(() => store.state.companiesLocationStore.data)
const storeOcShufoo = computed(() => store.state.companiesStoreOcShufoo.data)
const storeOcShufooLoading = computed(() => store.state.companiesStoreOcShufoo.loading)
const storeOcMapionBiz = computed(() => store.state.companiesStoreOcMapionBiz.data)
const storeOcMapionBizLoading = computed(() => store.state.companiesStoreOcMapionBiz.loading)
const sites = computed<Site[]>(() => store.state.companiesSites.data)
const sitesLoading = computed<boolean>(() => store.state.companiesSites.loading)
const storeGmbConnected = computed<boolean>(() => store.state.companiesStoreGmb.connected)
const storeGmbLoading = computed<boolean>(() => store.state.companiesStoreGmb.loading)

/** Vuex Store: getters */
const hasAvailableProduct = computed<boolean>(() => store.getters['companiesCompany/hasAvailableProduct'])
const hasDocomoStore = computed<boolean>(() => store.getters['companiesCompany/hasDocomoStore'])
const providedFeaturesCompetitor = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesCompetitor'])
const providedFeaturesFoodMenu = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesFoodMenu'])
const providedFeaturesServicePattern = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesServicePattern'])
const providedFeaturesLinkUrl = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesLinkUrl'])
const providedFeaturesExternalAccount = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesExternalAccount'])
const canModifyServiceList = computed<boolean>(() => store.getters['companiesStoreGmbMetadata/canModifyServiceList'])
const providedFeaturesProduct = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesProduct'])
const providedFeaturesStoreOption = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesStoreOption'])
const providedFeaturesStoreSocial = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesStoreSocial'])
const providedFeaturesShufoo = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesShufoo'])
const providedFeaturesMapionBiz = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesMapionBiz'])
const providedFeaturesGmb = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesGmb'])
const canHaveFoodMenus = computed<boolean>(() => store.getters['companiesCompany/canHaveFoodMenus'])
const isAdmin = computed<boolean>(() => store.getters['companiesAccessibleApi/isAdmin'])
const isDistributer = computed<boolean>(() => store.getters['companiesAccessibleApi/isDistributer'])
const showOnlyOwnerAuthority = computed<boolean>(() => store.getters['companiesAccessibleApi/showOnlyOwnerAuthority'])
const isRettyPlan = computed<string>(() => store.getters['companiesCompany/isRettyPlan'])
const taishuTenpyoOption = computed<boolean>(() => store.getters['companiesCompany/taishuTenpyoOption'])

/** Vuex Store: action */
const readLocationStore = (payload: ReadLocationStoreParams) => store.dispatch('companiesLocationStore/readStore', payload)
const readStoreOcShufoo = () => store.dispatch('companiesStoreOcShufoo/readStoreOcShufoo')
const readStoreOcMapionBiz = () => store.dispatch('companiesStoreOcMapionBiz/readStoreOcMapionBiz')
const readSites = (payload: ReadSitesParams) => store.dispatch('companiesSites/readSites', payload)
const readStoreGmb = (payload: ReadStoreGmbParams) => store.dispatch('companiesStoreGmb/readStoreGmb', payload)
const readStoreGmbMetadata = (storeUuid: string) => store.dispatch('companiesStoreGmbMetadata/readStoreGmbMetadata', storeUuid)

/** Lifecycle */
if (providedFeaturesExternalAccount.value && providedFeaturesLinkUrl.value) {
  readConnects({ companyUuid: companyUuid.value, storeUuid: storeUuid.value })
  readSites({ company_uuid: companyUuid.value, store_uuid: storeUuid.value })
}
readLocationStore({ company_uuid: companyUuid.value, store_uuid: storeUuid.value })
readStoreDocomo({ companyUuid: companyUuid.value, storeUuid: storeUuid.value })
if(providedFeaturesShufoo.value) readStoreOcShufoo()
if(providedFeaturesShufoo.value) readStoreOcMapionBiz()
if(providedFeaturesGmb.value) {
  readStoreGmb({ company_uuid: companyUuid.value, store_uuid: storeUuid.value })
  readStoreGmbMetadata(storeUuid.value)
}

/** Watch */
watch(locationStore, () => {
  if (!locationStore.value) return

  emit('update-title', t('pages.LocationStoreConnects.title', {
    name: locationStore.value.attributes.name,
  }))

  const { business_type_name, business_type_id } = locationStore.value.attributes
  businessTypes[business_type_name] = business_type_id
  isBusinessType.value = business_type_id
})

/** Methods */
const changeBusinessType = (value: string) => {
  isBusinessType.value = Number(value)
}

const openUpgradeModal = (siteType: string) => {
  isOpenUpgradeModal.value = true
  openUpgradeModalService.value = siteType
}

const submitUpgradeModal = () => {
  window.open('https://kutikomi.com/contact?kind=introduction', '_blank')
}
</script>

<template>
  <KkBox tag="div">
    <template #header>
      <ul class="storeConnects__tabs">
        <li v-for="tab in tabs" :key="tab">
          <strong v-if="tab === 'connection'">
            {{ $t(`pages.LocationStoreConnects.tabs.${ tab }` ) }}
          </strong>
          <router-link
            v-else
            :to="{
              name: 'CompaniesLocationStore',
              params: { company_uuid: companyUuid, store_uuid: storeUuid },
              query: { tab },
            }"
          >
            {{ $t(`pages.LocationStoreConnects.tabs.${ tab }` ) }}
          </router-link>
        </li>
      </ul>
    </template>
    <template #content>
      <div class="storeConnects__container">
        <KkInfo icon="hint">
          {{ t('pages.LocationStoreConnects.info') }}
        </KkInfo>
        <KkLoading
          v-if="isLoading || storeDocomoLoading || storeOcShufooLoading || storeOcMapionBizLoading || sitesLoading || storeGmbLoading"
          min-height="120px"
        />
        <template v-else>
          <div class="storeConnects__selectCategory">
            <KkSelect
              :options="businessTypes"
              :model-value="isBusinessType"
              @update:model-value="changeBusinessType"
            />
          </div>
          <table class="storeConnects__sites">
            <tbody>
              <KkConnectSite
                v-for="site in filteredSites"
                :key="site.siteType"
                :site="site"
                @open-upgrade-modal="openUpgradeModal"
              />
            </tbody>
          </table>
        </template>
      </div>
    </template>
  </KkBox>
  <KkUpgradeModal
    :is-visible="isOpenUpgradeModal"
    :site-type="openUpgradeModalService"
    @submit="submitUpgradeModal"
    @close="isOpenUpgradeModal = false"
  />
</template>

<style lang="scss" scoped>
@import '../../../../stylesheets/biz_spa/foundation/var';
@import '../../../../stylesheets/biz_spa/foundation/mixin';
.storeConnects {
  $root: #{&};
  &__tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 32px;
    a, strong {
      font-size: 14px;
      line-height: 1;
      letter-spacing: 1px;
      white-space: nowrap;
    }
    a {
      color: $color-accent-primary;
      text-decoration: none;
    }
    strong {
      font-weight: normal;
    }
  }
  &__selectCategory {
    max-width: 220px;
    margin-top: 24px;
  }
  &__sites {
    width: 100%;
    border-spacing: 0 8px;
    margin-top: 16px;
  }
  @media screen and (max-width: 991px) {
    &__tabs {
      gap: 16px;
    }
    &__sites {
      display: block;
      > tbody {
        display: block;
      }
    }
  }
}
</style>
